import React, { useState, useEffect } from "react";
import { AreaChartComponent } from "../../components/AreaChartComponent";
import InfoCardVertical from "../../components/InfoCardVertical";
import { GrGroup } from "react-icons/gr";
import { Leaderboard } from "../../components/Leaderboard";
import useUserStore2 from "../../stores/userStore2";
import { dashboardService } from "../../services/dashboardService";
import { Skeleton } from "../../components/ui/skeleton";
import { format, subDays } from "date-fns/esm";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { communityService } from "../../services";

const DashboardCom = ({ communityId: propCommunityId }) => {
  const { user } = useUserStore2();
  const [totalMembers, setTotalMembers] = useState(0);
  const [percentageChange, setPercentageChange] = useState("0.0");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState([]);
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [communityName, setCommunityName] = useState("");

  const communityId = user?.currentProfile?.memberships?.[0]?.communityId;

  // Determine which community to show data for
  const getActiveCommunity = () => {
    // Priority 1: Explicitly passed prop
    if (propCommunityId) return propCommunityId;

    // Priority 2: URL parameter (e.g., /comm-dashboard/:communityId)
    if (params.communityId) return params.communityId;

    // Priority 3: Current profile's first membership
    return user?.currentProfile?.memberships?.[0]?.communityId;
  };

  const activeCommunityId = getActiveCommunity();
  const activeMembership = user?.currentProfile?.memberships?.find(
    (m) => m.communityId === activeCommunityId
  );

  console.log("active community: ", activeCommunityId);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        if (!activeCommunityId) {
          setError("No community selected");
          setLoading(false);
          return;
        }

        const startDate = format(subDays(new Date(), 90), "yyyy-MM-dd");
        const endDate = format(new Date(), "yyyy-MM-dd");

        const [usersResponse, chartResponse] = await Promise.all([
          dashboardService.countCommunityUsers(activeCommunityId),
          dashboardService.countCommunityContributions(
            activeCommunityId,
            startDate,
            endDate
          ),
        ]);

        // Process members data
        const membersData = usersResponse[0];
        setTotalMembers(membersData?.total_members || 0);
        setPercentageChange(
          membersData?.total_members_old
            ? (
                ((membersData.total_members - membersData.total_members_old) /
                  membersData.total_members_old) *
                100
              ).toFixed(1)
            : "0.0"
        );

        // Format area chart data
        const formattedChartData = chartResponse.map((item) => ({
          date: item._id,
          total_quantities: item.total_quantities,
          points: item.total_points,
        }));
        setChartData(formattedChartData);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [activeCommunityId, params.communityId, propCommunityId]);

  useEffect(() => {
    const fetchCommunityName = async () => {
      try {
        // Skip if we already have the name in location state
        if (location.state?.communityName) {
          setCommunityName(location.state.communityName);
          return;
        }

        const response = await communityService.getCommunityDetails(
          communityId
        );

        if (response?.Data?.length > 0) {
          const name = response.Data[0].name;
          setCommunityName(name);
          // Update navigation state while preserving any existing state
          navigate(location.pathname, {
            ...location,
            state: {
              ...location.state,
              communityName: name,
            },
            replace: true,
          });
          // console.log("Fetched community name:", name);
          // console.log("Current location state:", location.state);
        }
      } catch (error) {
        console.error("Error fetching community name:", error);
      }
    };

    if (communityId) {
      fetchCommunityName();
    }
  }, [communityId, navigate, location]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen">
      <div className="flex-1 flex flex-col">
        <div className="p-4 sm:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-7 gap-8">
            {/* Community Members Card */}
            <div className="col-span-5 lg:col-span-2 h-full">
              {loading ? (
                <div className="bg-white p-6 rounded-lg shadow h-full">
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-8 w-full mb-4" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              ) : (
                <div className="h-full w-full">
                  <InfoCardVertical
                    heading="Total Community Members"
                    number={error ? "N/A" : totalMembers.toLocaleString()}
                    icon={GrGroup}
                    bottomText={error || "Updated Today"}
                    bgColor={error ? "bg-red-100" : "bg-blue-100"}
                    iconColor={error ? "text-red-600" : "text-blue-600"}
                    trend={{
                      percentage: percentageChange + "%",
                      isUp: percentageChange >= 0,
                    }}
                    className="h-full"
                  />
                </div>
              )}
            </div>

            {/* Area chart */}
            <div className="relative col-span-5 h-full">
              <div className="h-full w-full">
                <AreaChartComponent data={chartData} loading={loading} />
              </div>
            </div>
          </div>
        </div>

        <Leaderboard
          title={`${
            activeMembership?.communityName || "Community"
          } Leaderboard`}
          communityId={activeCommunityId}
        />
      </div>
    </div>
  );
};

export default DashboardCom;
